import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getNoteReceivableDetails, collectNoteReceivable, cancelNoteReceivable, getBanks, clearError, clearSuccess, clearNoteReceivableDetails } from '../../../../redux/Slices/authSlice';

// note receivable details component
const NoteReceivableDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { noteReceivableDetails, banks, isLoading, error, success } = useSelector((s) => s.auth);

  const [collectForm, setCollectForm] = useState({
    bank_account_id: '',
    amount: '',
    collection_date: '',
  });

  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const bankSearchInputRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });

  useEffect(() => {
    dispatch(getNoteReceivableDetails(id));
    dispatch(getBanks({ per_page: 10 }));
    return () => {
      dispatch(clearNoteReceivableDetails());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      dispatch(getNoteReceivableDetails(id));
    }
  }, [success, dispatch, id]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  const filteredBanks = Array.isArray(banks)
    ? banks.filter((b) => {
      const term = (bankSearchTerm || '').toLowerCase();
      const code = (b.code || '').toLowerCase();
      const nameAr = (b.name_ar || '').toLowerCase();
      const nameEn = (b.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    })
    : [];

  const handleCollect = (e) => {
    e.preventDefault();
    if (!collectForm.bank_account_id || !collectForm.amount || !collectForm.collection_date) {
      toast.error('برجاء استكمال بيانات التحصيل', { rtl: true });
      return;
    }
    dispatch(
      collectNoteReceivable({
        id,
        data: {
          bank_account_id: Number(collectForm.bank_account_id),
          amount: Number(collectForm.amount),
          collection_date: collectForm.collection_date,
        },
      })
    );
  };

  const handleCancel = () => {
    dispatch(cancelNoteReceivable(id));
  };

  useEffect(() => {
    if (bankDropdownOpen && bankSearchInputRef.current) {
      bankSearchInputRef.current.focus();
    }
  }, [bankDropdownOpen]);

  const nr = noteReceivableDetails || {};

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: '20px' }}>تفاصيل ورقة القبض</h1>
        <button
          onClick={() => navigate('/notes-receivable')}
          style={{ background: '#444', color: 'white', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
        >
          رجوع
        </button>
      </div>

      <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div><strong>العميل:</strong> <span>{nr.customer?.name || nr.customer_name || '—'}</span></div>
          <div><strong>رقم الورقة:</strong> <span>{nr.note_number || '—'}</span></div>
          <div><strong>القيمة:</strong> <span>{nr.amount != null ? Number(nr.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</span></div>
          <div><strong>الحالة:</strong> <span>{nr.status || '—'}</span></div>
          <div><strong>تاريخ الإصدار:</strong> <span>{nr.issue_date ? String(nr.issue_date).split('T')[0] : '—'}</span></div>
          <div><strong>تاريخ الاستحقاق:</strong> <span>{nr.due_date ? String(nr.due_date).split('T')[0] : '—'}</span></div>
          <div><strong>قيد اليومية:</strong> <span>{nr.journal_entry_id || '—'}</span></div>
          <div><strong>قيد تحصيل الورقة:</strong> <span>{nr.collection_journal_entry_id || '—'}</span></div>
          <div><strong>أنشئت بواسطة:</strong> <span>{nr.creator?.name || '—'}</span></div>
          <div><strong>تاريخ الإنشاء:</strong> <span>{nr.created_at ? String(nr.created_at).split('T')[0] : '—'}</span></div>
          <div><strong>تاريخ التحديث:</strong> <span>{nr.updated_at ? String(nr.updated_at).split('T')[0] : '—'}</span></div>
          <div style={{ gridColumn: '1 / -1' }}><strong>الوصف:</strong> <span>{nr.description || '—'}</span></div>
        </div>
      </div>

      <form
        onSubmit={handleCollect}
        style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 16, display: 'grid', gap: 12, marginBottom: 16 }}
      >
        <h3 style={{ margin: 0, marginBottom: 8, fontSize: '20px' }}>تحصيل ورقة القبض</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>
              البنك <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <div
              onClick={() => setBankDropdownOpen((p) => !p)}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <span>
                {collectForm.bank_account_id
                  ? (() => {
                    const b = (banks || []).find((x) => String(x.id) === String(collectForm.bank_account_id));
                    return b ? `${b.code ? `${b.code} - ` : ''}${b.name_ar || b.name || ''}` : 'اختر البنك';
                  })()
                  : 'اختر البنك'}
              </span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            {bankDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  marginTop: 4,
                  maxHeight: 260,
                  overflowY: 'auto',
                }}
              >
                <div style={{ padding: 8 }}>
                  <input
                    ref={bankSearchInputRef}
                    type="text"
                    placeholder="ابحث عن بنك"
                    value={bankSearchTerm}
                    onChange={(e) => setBankSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 8,
                      backgroundColor: '#14263b',
                      border: '1px solid #444',
                      borderRadius: 6,
                      color: 'white',
                      fontSize: 14,
                    }}
                  />
                </div>
                <div>
                  {filteredBanks.map((b) => (
                    <div
                      key={b.id}
                      onClick={() => {
                        setCollectForm((prev) => ({ ...prev, bank_account_id: b.id }));
                        setBankDropdownOpen(false);
                      }}
                      style={{
                        padding: 10,
                        cursor: 'pointer',
                        borderTop: '1px solid #333',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{`${b.code ? `${b.code} - ` : ''}${b.name_ar || b.name || ''}`}</span>
                    </div>
                  ))}
                  {filteredBanks.length === 0 && (
                    <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>لا توجد نتائج</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>
              قيمة التحصيل <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="number"

              value={collectForm.amount}
              onChange={(e) => setCollectForm((p) => ({ ...p, amount: e.target.value }))}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>
              تاريخ التحصيل <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="date"
              value={collectForm.collection_date}
              onChange={(e) => setCollectForm((p) => ({ ...p, collection_date: e.target.value }))}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            إلغاء التحصيل
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            تحصيل
          </button>
        </div>
      </form>
    </div>
  );
};

export default NoteReceivableDetails;


